import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePetitionActionsPageData, usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import { useSupporterVoicesVideoFirstTrackingProps } from '#src/app/pages/actions/shared/hooks';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { useVideoContext, useVideosComponentContext } from '../../../../../../context';
import { useCamera } from '../../../../../../shared/hooks/useCamera';
import { useSkipRecording } from '../../../SkipRecording/hooks/useSkipRecording';

import { getLocationLabel } from './getLocationLabel';

type Result = ModelHookResult<
	{
		/**
		 * Handed over rather than interpolated here so the headline's i18n keys stay literal where they
		 * are rendered, and `no-invalid-translation-keys` can still check them.
		 */
		location: string | undefined;
		shouldAnimateEntrance: boolean;
	},
	{
		handleRecord: () => void;
		handleSkip: () => void;
	}
>;

export function useVideoFirstAsk(): Result {
	const { track } = useCampaignTracking();
	const {
		data: {
			petition: { relevantLocationLocalizedName },
		},
	} = usePetitionActionsPageData();
	const [, { setShareSupporterVoiceSuccess }] = usePetitionActionsPageState();
	const {
		data: { hasPlayedAskIntro },
		actions: { setHasPlayedAskIntro, setUploadStep, setErrorMessage },
	} = useVideosComponentContext();
	const {
		actions: { setBlob },
	} = useVideoContext();
	const { openCamera } = useCamera();
	const {
		actions: { handleSkip, goToCommentStep },
	} = useSkipRecording();
	const trackingProps = useSupporterVoicesVideoFirstTrackingProps();

	// Captured at first render: flipping the context flag below must not cancel a cascade that is
	// still running, and a re-entry mid-flow has to land everything already in place.
	const [shouldAnimateEntrance] = useState(() => !hasPlayedAskIntro);

	useEffect(() => {
		setHasPlayedAskIntro(true);
	}, [setHasPlayedAskIntro]);

	/**
	 * Permission first, navigation second. Control advances to the recorder and asks there, leaving
	 * anyone who declines on a recorder they cannot use; here a decline carries on to the comment step.
	 */
	const handleRecord = useCallback(() => {
		const requestAndAdvance = async () => {
			track('campaign_action_create_video_add_video_click', {
				source: 'campaign_action_landing',
				...trackingProps,
			});
			setShareSupporterVoiceSuccess(false);
			setBlob(null);

			const stream = await openCamera();
			if (stream) {
				setUploadStep('recorder');
				return;
			}

			// `openCamera` leaves behind an explanation of how to grant permission, which belongs on the
			// recorder, not on the comment form the supporter is about to see.
			setErrorMessage(null);
			goToCommentStep();
		};

		void requestAndAdvance();
	}, [
		goToCommentStep,
		openCamera,
		setBlob,
		setErrorMessage,
		setShareSupporterVoiceSuccess,
		setUploadStep,
		track,
		trackingProps,
	]);

	const location = useMemo(() => getLocationLabel(relevantLocationLocalizedName), [relevantLocationLocalizedName]);

	return {
		data: { location, shouldAnimateEntrance },
		actions: { handleRecord, handleSkip },
	};
}
